import { DestroyRef, inject, Injectable } from '@angular/core';
import { translate, TranslocoService } from '@jsverse/transloco';
import Highcharts, { Chart, Options, Series } from 'highcharts';
import 'highcharts/modules/accessibility';
import 'highcharts/modules/no-data-to-display';
import { merge } from 'lodash-es';
import { selectTranslate } from '../../i18n/portal-language.util';
import { Language } from '../../model/user/language.enum';
import { CommonUtil } from '../../util/common.util';

type ChartWithNoData = Chart & {
    hasData?: () => boolean;
    hideNoData?: () => void;
    showNoData?: () => void;
};
type SeriesWithDataTable = Series & {
    dataTable?: { rowCount?: number };
    options: { isInternal?: boolean };
};
type ChartPrototypeWithHasDataPatch = ChartWithNoData & { vcHasDataPatched?: boolean };
type ChartWithAccessibility = Chart & {
    accessibility?: {
        components?: {
            legend?: {
                recreateProxies?: () => boolean;
            };
        };
    };
};

@Injectable({
    providedIn: 'root',
})
export class HighchartsCommonService {
    private readonly _transloco = inject(TranslocoService);
    private readonly _destroyRef = inject(DestroyRef);

    constructor() {
        selectTranslate(this._transloco, () => this.refreshChartLanguage(), this._destroyRef);
    }

    get noDataAvailableText(): string {
        return translate('COMMON_UI.TABLE.NO_DATA_AVAILABLE');
    }

    get noDataTemplate(): string {
        return (
            '<div class="vc-chart-empty-container">' +
            '   <div class="vc-chart-no-data">' +
            '       <span class="material-icons-outlined vc-chart-empty-icon">bar_chart</span>' +
            `       <span class="vc-chart-no-data-text body-small">${this.noDataAvailableText}</span>` +
            '   </div>' +
            '   <div class="vc-chart-empty-background" style="width: 100%; height: 80px"></div>' +
            '</div>'
        );
    }

    get options(): Options {
        return {
            chart: {
                borderWidth: 1,
                borderRadius: 12,
                spacingLeft: 20,
                spacingRight: 20,
                style: {
                    fontFamily: 'var(--primary-font)',
                },
            },
            plotOptions: {
                series: {
                    dataLabels: {
                        style: {
                            fontSize: '10px',
                            fontWeight: '400',
                        },
                        useHTML: true,
                    },
                    borderWidth: 1,
                },
            },
            title: {
                useHTML: true,
                style: {
                    fontSize: '16px',
                    fontWeight: '500',
                    align: 'left',
                },
            },
            lang: {
                thousandsSep: ',',
                noData: this.noDataTemplate,
                locale: this.getHighchartsLocale(),
                accessibility: this.getAccessibilityLangOptions(),
            },
            legend: {
                itemStyle: {
                    fontSize: '12px',
                    fontWeight: '400',
                    height: 16,
                    padding: '1px 2px',
                },
                itemHoverStyle: {
                    borderRadius: '4px',
                },
                symbolPadding: 4,
                useHTML: true,
            },
            tooltip: {
                padding: 6,
                borderWidth: 1,
                borderRadius: 4,
                shadow: {
                    color: '#1b203345',
                    opacity: 0.27,
                    offsetX: -2,
                    offsetY: 2,
                },
                style: {
                    fontSize: '11px',
                },
                followPointer: true,
            },
            yAxis: {
                labels: {
                    style: {
                        fontSize: '10px',
                    },
                },
                title: {
                    style: {
                        fontSize: '10px',
                    },
                },
            },
            xAxis: {
                labels: {
                    style: {
                        fontSize: '10px',
                    },
                    useHTML: true,
                },
                title: {
                    style: {
                        fontSize: '10px',
                    },
                },
            },
            noData: {
                useHTML: true as unknown as object,
            },
            exporting: {
                enabled: false,
            },
            credits: {
                enabled: false,
            },
            accessibility: {
                enabled: true,
                keyboardNavigation: {
                    enabled: true,
                    focusBorder: {
                        style: {
                            borderRadius: '4px',
                            color: CommonUtil.getRenderedColor('--border-high-contrast'),
                            lineWidth: 2,
                        },
                    },
                    seriesNavigation: {
                        mode: 'normal',
                    },
                },
            },
        };
    }

    get colorOptions(): Options {
        return {
            chart: {
                backgroundColor: CommonUtil.getRenderedColor('--surface-1'),
                borderColor: CommonUtil.getRenderedColor('--border-low-contrast'),
                style: {
                    color: CommonUtil.getRenderedColor('--text-primary'),
                },
            },
            plotOptions: {
                series: {
                    dataLabels: {
                        style: {
                            color: CommonUtil.getRenderedColor('--text-primary'),
                        },
                    },
                    borderColor: CommonUtil.getRenderedColor('--surface-1'),
                },
            },
            title: {
                useHTML: true,
                style: {
                    color: CommonUtil.getRenderedColor('--text-primary'),
                },
            },
            legend: {
                itemStyle: {
                    color: CommonUtil.getRenderedColor('--text-primary'),
                    backgroundColor: 'transparent',
                },
                itemHoverStyle: {
                    color: CommonUtil.getRenderedColor('--text-primary'),
                    backgroundColor: CommonUtil.getRenderedColor('--button-secondary-bg-hover'),
                },
                navigation: {
                    activeColor: CommonUtil.getRenderedColor('--button-icon-secondary-color'),
                    inactiveColor: CommonUtil.getRenderedColor('--button-disabled-bg'),
                },
            },
            tooltip: {
                backgroundColor: CommonUtil.getRenderedColor('--surface-2'),
                borderColor: CommonUtil.getRenderedColor('--border-mid-contrast'),
                style: {
                    color: CommonUtil.getRenderedColor('--text-primary'),
                },
            },
            yAxis: {
                labels: {
                    style: {
                        color: CommonUtil.getRenderedColor('--text-primary'),
                        backgroundColor: CommonUtil.getRenderedColor('--surface-1'),
                    },
                },
                title: {
                    style: {
                        color: CommonUtil.getRenderedColor('--text-primary'),
                    },
                },
            },
            xAxis: {
                labels: {
                    style: {
                        color: CommonUtil.getRenderedColor('--text-primary'),
                    },
                },
                title: {
                    style: {
                        color: CommonUtil.getRenderedColor('--text-primary'),
                    },
                },
            },
        };
    }

    initializeHighCharts() {
        Highcharts.setOptions(merge({}, this.options, this.colorOptions));
        this.patchChartHasData();
    }

    refreshChartLanguage(): void {
        this.initializeHighCharts();
        const noData = this.noDataTemplate;
        const locale = this.getHighchartsLocale();
        const accessibility = this.getAccessibilityLangOptions();
        Highcharts.charts.forEach((chart: Chart | undefined) => {
            if (!chart) {
                return;
            }
            chart.update({ lang: { noData, locale, accessibility } }, false);
            this.refreshLegendAccessibilityLabels(chart);
            const chartWithNoData = chart as ChartWithNoData;
            const hasNoSeriesData = chart.series.every((series) => series.data.length === 0);
            if (hasNoSeriesData && chartWithNoData.showNoData) {
                chartWithNoData.hideNoData?.();
                chartWithNoData.showNoData();
            }
        });
    }

    redrawChartColors() {
        Highcharts.charts.forEach((chart: Chart | undefined) => {
            if (chart) {
                chart.update({ ...this.colorOptions });
            }
        });
    }

    static getRenderedColor(color: string): string {
        return getComputedStyle(document.documentElement).getPropertyValue(color);
    }

    private patchChartHasData(): void {
        const chartProto = Highcharts.Chart?.prototype as ChartPrototypeWithHasDataPatch | undefined;
        const originalHasData = chartProto?.hasData;
        if (!chartProto || !originalHasData || chartProto.vcHasDataPatched) {
            return;
        }

        chartProto.vcHasDataPatched = true;
        chartProto.hasData = function (this: Chart): boolean {
            return HighchartsCommonService.hasUnderlyingSeriesData(this) || originalHasData.call(this);
        };
    }

    private static hasUnderlyingSeriesData(chart: Chart): boolean {
        return (chart.series ?? []).some((series) => {
            const seriesWithData = series as SeriesWithDataTable;
            if (seriesWithData.options.isInternal) {
                return false;
            }
            return (seriesWithData.dataTable?.rowCount ?? seriesWithData.data?.length ?? 0) > 0;
        });
    }

    private getHighchartsLocale(): string {
        switch (this._transloco.getActiveLang() as Language) {
            case Language.FR:
                return 'fr-FR';
            case Language.ES:
                return 'es-ES';
            default:
                return 'en-US';
        }
    }

    private getAccessibilityLangOptions(): NonNullable<Options['lang']>['accessibility'] {
        return {
            legend: {
                legendItem: translate('COMMON_UI.CHART.LEGEND_SHOW_ITEM'),
            },
        };
    }

    private refreshLegendAccessibilityLabels(chart: Chart): void {
        (chart as ChartWithAccessibility).accessibility?.components?.legend?.recreateProxies?.();
    }

    destroy(): void {
        // Kept for backward compatibility; subscriptions use DestroyRef.
    }
}
